import api from "@/libs/axios";
import { QueryConfig } from "@/libs/query";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { apiEnum } from "@shared/types/enums/apiEnum";
import { OrganizerSettingEntity } from "./entities/organizerSettingEntity";
import { isAxiosError } from "axios";
import { QUERY_KEY } from "@/pages/taskpane/shared/constants/queryKey";

class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
    this.name = "ApiError";
  }
}

const get = async (email: string): Promise<OrganizerSettingEntity> => {
  try {
    const queryString = apiEnum.organizerSetting + "/" + email + "?clearCache=true";
    const { data } = await api.get(queryString);
    // console.log("📥 Fetched organizer setting from API:", data.data);
    return new OrganizerSettingEntity(data.data);
  } catch (error) {
    if (isAxiosError(error)) {
      const status = error.response?.status || 500;
      const message = error.response?.data?.message || error.message || "Failed to fetch organizer setting";
      throw new ApiError(message, status);
    }
    throw error;
  }
};

export const getOptions = (email: string) =>
  queryOptions({
    queryKey: [QUERY_KEY.GET_ORGANIZER_SETTING, email],
    queryFn: () => get(email),

    // ✅ Cache strategy: Cache for long time, only refetch on manual invalidation
    // staleTime: 2 * 60 * 1000, // Cache is fresh for 2 minutes
    // gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    // refetchOnMount: false, // Never refetch on mount (use cache)
    // refetchOnWindowFocus: false, // Never refetch on window focus
    // refetchOnReconnect: false, // Never refetch on reconnect (use cache) keep cache
  });

type UseGetOrganizerSettingType = {
  queryConfig?: QueryConfig<typeof getOptions>;
  email: string;
};

export const useGetOrganizerSetting = ({ queryConfig, email }: UseGetOrganizerSettingType) => {
  const query = useQuery({
    ...getOptions(email),
    ...queryConfig,
    enabled: !!email,
  });

  // Debug logging for cache behavior
  if (process.env.NODE_ENV === 'development') {
    // console.log("📦 Organizer Setting Query:", {
    //   hasData: !!query.data,
    //   isLoading: query.isLoading,
    //   isFetching: query.isFetching,
    //   isRefetching: query.isRefetching,
    //   fromCache: !query.isLoading && !query.isFetching && !!query.data,
    // });
  }

  return query;
};
